import {
  CellValues,
  ColumnHeading,
  EntityFilterControlsConfig,
  MultiSelectOption,
  PaginatedDataTable,
  TableCellStylesFn,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { formatDMYWithTime } from '@lbcamden/shared-utils';
import { FunctionComponent, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { SectionContainer } from '~/client/components/common/SectionContainer';
import { DirectoryUserInfo } from '~/client/components/shared/DirectoryUserInfo';
import { useReferenceListSelectOptions } from '~/client/hooks/storeHooks';
import { toNotePage } from '~/client/utils/navigation';
import { Note, UsedNoteType } from '~/common/types/notes';

type Props = {
  propertyId: number;
  notes: Note[];
  /** default 'all' */
  typeFilterOptions?: UsedNoteType[] | 'all';
  headerActions?: ReactNode;
  getNoteSubject?(note: Note): ReactNode;
};

type ColumnId = StringKey<Note>;

const headings = [
  { id: 'caution', content: 'Caution' },
  { id: 'subject', content: 'Subject' },
  { id: 'created', content: 'Created' },
  { id: 'createdBy', content: 'Created by' },
] as const satisfies ColumnHeading<ColumnId>[];

const getFilterConfigs = (typeOptions: MultiSelectOption[]): EntityFilterControlsConfig<Note> =>
  [
    {
      type: 'toggle',
      key: 'caution',
      label: 'Caution',
    },
    typeOptions.length && {
      type: 'multi-select',
      key: 'type',
      label: 'Type',
      options: typeOptions,
    },
  ].filter(Boolean) as EntityFilterControlsConfig<Note>;

const baseMapNoteToRow = (note: Note): CellValues<ColumnId> => ({
  ...note,
  _rowId: note.noteId,
  caution: note.caution ? <strong>Yes</strong> : 'No',
  created: formatDMYWithTime(note.created),
  createdBy: <DirectoryUserInfo camdenAccountName={note.createdBy} />,
});

const cellStyles: TableCellStylesFn = (columnId) => [{ width: columnId === 'subject' ? 200 : 50 }];

export const NotesTable: FunctionComponent<Props> = ({
  propertyId,
  notes,
  typeFilterOptions = 'all',
  headerActions,
  getNoteSubject,
}) => {
  const navigate = useNavigate();

  const referenceListSelectOptions = useReferenceListSelectOptions();

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: headings.slice(0, 2),
    sm: headings.slice(0, 2),
    md: headings.slice(0, 3),
  });

  const noteTypeOptions = referenceListSelectOptions('COMBNOTOBJ').filter(
    ({ value }) =>
      typeFilterOptions === 'all' || typeFilterOptions?.includes(value as UsedNoteType),
  );

  const onClickRow = useCallback(
    ({ noteId }: Note) => navigate(toNotePage(propertyId, noteId)),
    [],
  );

  const mapNoteToRow = useCallback(
    (note: Note) => ({
      ...baseMapNoteToRow(note),
      subject: getNoteSubject?.(note) || note.subject,
    }),
    [getNoteSubject],
  );

  return (
    <SectionContainer title="Notes" id="notes" headerActions={headerActions}>
      <PaginatedDataTable
        cellStyles={cellStyles}
        headings={visibleHeadings}
        filterConfigs={getFilterConfigs(noteTypeOptions)}
        items={notes}
        defaultSort={{ field: 'created', direction: 'desc' }}
        mapItemToRow={mapNoteToRow}
        onClickRow={onClickRow}
      />
    </SectionContainer>
  );
};
